import React, { useContext } from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps } from 'react-router';
import { Form, Formik, FormikProps } from 'formik';
import { useQuery, useMutation } from '@apollo/client/react/hooks';

import Input from 'four-nets/ui-v2/form/input/input';
import GeoSelect from 'four-nets/geo/geo-select/geo-select';
import { AlertContext } from 'lib/alert-context/alert-context';

import DateSelect from '../date-select/date-select';
import defaultStyles from './settings-wedding.scss';
import query, {
    SettingsWeddingQuery,
    SettingsWeddingQueryVariables,
    WeddingNodeFragmentFragment,
} from './settings-wedding.graphql';
import mutation, { UpdateWeddingSettingsMutation, UpdateWeddingSettingsMutationVariables } from './mutation.graphql';

interface Values {
    partnerName: string;
    day: string;
    month: string;
    year: string;
    county?: string | null;
    country?: string | null;
    city?: string | null;
}

interface Props extends RouteComponentProps<{ username: string }> {
    styles: typeof defaultStyles;
}

const dayMonthYearFromDatestring = (datestring?: string | null) => {
    if (!datestring) {
        return { day: '0', month: '0', year: '0' };
    }
    const date = new Date(Date.parse(datestring));
    return {
        day: String(date.getDate()),
        month: String(date.getMonth()),
        year: String(date.getFullYear()),
    };
};

const datestringFromDayMonthYear = (day: string | null, month: string | null, year: string | null) => {
    if (!day || !month || !year) {
        return null;
    }

    const date = new Date();
    date.setDate(Number(day));
    date.setMonth(Number(month));
    date.setFullYear(Number(year));

    return date.toISOString();
};

const MAX_WEDDING_YEAR_DELTA = 5;

const SettingsWedding: React.FC<Props> = props => {
    const { fail } = useContext(AlertContext);

    const maxYear = new Date().getFullYear() + MAX_WEDDING_YEAR_DELTA;

    const { styles } = props;

    const { data } = useQuery<SettingsWeddingQuery, SettingsWeddingQueryVariables>(query);
    const [updateWedding] = useMutation<UpdateWeddingSettingsMutation, UpdateWeddingSettingsMutationVariables>(
        mutation
    );

    if (!data?.viewer?.photoBlog?.details) {
        return null;
    }

    const details = data.viewer.photoBlog.details as WeddingNodeFragmentFragment;

    return (
        <div>
            <Formik
                initialValues={{
                    partnerName: details.partnerName ? details.partnerName : '',
                    county: (details.county && details.county.id) || null,
                    country: (details.country && details.country.id) || null,
                    city: details.weddingCity,
                    ...dayMonthYearFromDatestring(details.weddingDate),
                }}
                onSubmit={(values, { resetForm }) => {
                    const variables = {
                        input: {},
                    } as UpdateWeddingSettingsMutationVariables;

                    if (values.partnerName) {
                        variables.input.partnerName = values.partnerName;
                    }

                    if (values.city) {
                        variables.input.weddingCity = values.city;
                    }

                    variables.input.weddingDate = datestringFromDayMonthYear(values.day, values.month, values.year);

                    if (values.county && values.country) {
                        variables.input.countyId = values.county;
                    } else {
                        variables.input.countyId = values.county;
                        variables.input.countryId = values.country;
                    }

                    updateWedding({ variables })
                        .then(result => {
                            const wedding =
                                result.data && result.data.updateWedding && result.data.updateWedding.wedding;

                            if (wedding) {
                                resetForm({
                                    values: {
                                        partnerName: wedding.partnerName || '',
                                        county: (wedding.county && wedding.county.id) || null,
                                        country: (wedding.country && wedding.country.id) || null,
                                        city: wedding.weddingCity,
                                        ...dayMonthYearFromDatestring(wedding.weddingDate),
                                    },
                                });
                            } else {
                                resetForm();
                            }
                        })
                        .catch(reason => {
                            fail!({
                                body: JSON.stringify(reason),
                                onAccept: () => window.location.reload(true),
                            });
                        });
                }}
            >
                {({ values, setFieldValue, isValid, dirty, isSubmitting, getFieldProps }: FormikProps<Values>) => (
                    <Form>
                        <FormattedMessage id='WEDDING_DATE' />
                        <DateSelect
                            dayField={getFieldProps('day')}
                            monthField={getFieldProps('month')}
                            yearField={getFieldProps('year')}
                            allowZero={true}
                            yearRange={{ min: 1980, max: maxYear }}
                        />
                        <div className={styles.geoWrapper}>
                            <div className={styles.countyWrapper}>
                                <label>
                                    <FormattedMessage id='WEDDING_LOCATION'>
                                        {msg => <span className={styles.labelText}>{msg}</span>}
                                    </FormattedMessage>
                                    <GeoSelect
                                        {...getFieldProps('county')}
                                        countyId={values.county}
                                        countryId={values.country}
                                        allowCountrySelection={true}
                                        onChange={(selectedCountyID, selectedCountryID) => {
                                            setFieldValue('county', selectedCountyID);
                                            setFieldValue('country', selectedCountryID);
                                        }}
                                    />
                                </label>
                            </div>
                            <div className={styles.cityWrapper}>
                                <label htmlFor='city'>
                                    <FormattedMessage id='WHERE_LIVING_CITY'>
                                        {msg => <span className={styles.labelText}>{msg}</span>}
                                    </FormattedMessage>
                                </label>
                                <Input style={{ wrapper: styles.inputWrapper }} {...getFieldProps('city')} />
                            </div>
                        </div>
                        <div>
                            <label htmlFor='partnerName'>
                                <FormattedMessage id='NAME_OF_SIGNIFICANT_OTHER'>
                                    {msg => <span className={styles.labelText}>{msg}</span>}
                                </FormattedMessage>
                                <Input style={{ wrapper: styles.inputWrapper }} {...getFieldProps('partnerName')} />
                            </label>
                        </div>
                        <button disabled={!isValid || !dirty || isSubmitting} className={styles.submitButton}>
                            <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default Styled(defaultStyles)(SettingsWedding);
